import { useMemo } from 'react';
import * as FlowcoreUI from '@flowcore-water/ui';
import { firebaseAuth, isDemoMode } from '../firebase';
import type { User } from '../types';
import { monitoring } from './monitoringService';
import { Sentry } from '../instrument';

type MaybePromise<T> = T | Promise<T>;

type RecentBugError = {
  message: string;
  stack?: string | null;
  timestamp?: number | null;
  eventId?: string | null;
};

type BugReportUserContext = {
  uid?: string | null;
  email?: string | null;
  displayName?: string | null;
  role?: string | null;
};

type BugReportReleaseInfo = {
  version?: string | null;
  build?: string | null;
  commit?: string | null;
  environment?: string | null;
};

type BugReportSubmissionPayload = {
  appSlug: string;
  summary: string;
  comments: string;
  url: string;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
    devicePixelRatio: number;
  };
  route: {
    path?: string;
    routeParams?: Record<string, string>;
    queryParams?: Record<string, string>;
    hash?: string | null;
  };
  user?: BugReportUserContext;
  release?: BugReportReleaseInfo;
  diagnostics?: {
    recentErrors?: RecentBugError[];
    breadcrumbs?: unknown[];
    sentry?: {
      lastEventId?: string | null;
      replayId?: string | null;
    };
    extra?: Record<string, unknown>;
  };
  appState?: unknown;
  screenshot?: {
    dataUrl: string;
    source: 'automatic' | 'manual';
    width: number;
    height: number;
    capturedAt: string;
  };
  submittedAt: string;
};

type BugReportSubmitter = (payload: BugReportSubmissionPayload) => Promise<{
  ticketId: string;
  status?: string;
  githubIssueUrl?: string | null;
  duplicateOfTicketId?: string | null;
}>;

type BugReportConfig = {
  appSlug: string;
  modalTitle?: string;
  submitReport: BugReportSubmitter;
  getReporter?: () => MaybePromise<BugReportUserContext | undefined>;
  getRelease?: () => MaybePromise<BugReportReleaseInfo | undefined>;
  getStateSnapshot?: () => MaybePromise<unknown>;
  getDiagnostics?: () => MaybePromise<BugReportSubmissionPayload['diagnostics'] | undefined>;
};

type BugReportSubmitterOptions = {
  getAuthToken: () => Promise<string | null | undefined> | string | null | undefined;
  apiBaseUrl?: string;
};

const DEFAULT_IDENTITY_API_BASE = 'https://us-central1-flowcore-st-mirror.cloudfunctions.net/identity';
const sharedBugReporting = FlowcoreUI as typeof FlowcoreUI & {
  createIdentityBugReportSubmitter?: (options: BugReportSubmitterOptions) => BugReportSubmitter;
};

function getReplayId(): string | null {
  const sentryWithReplay = Sentry as unknown as {
    getReplay?: () => { getReplayId?: () => string | undefined } | undefined;
  };
  return sentryWithReplay.getReplay?.()?.getReplayId?.() ?? null;
}

function createIdentitySubmitter({
  getAuthToken,
  apiBaseUrl,
}: BugReportSubmitterOptions): BugReportSubmitter {
  if (sharedBugReporting.createIdentityBugReportSubmitter) {
    return sharedBugReporting.createIdentityBugReportSubmitter({
      getAuthToken,
      apiBaseUrl,
    });
  }

  const baseUrl = (apiBaseUrl && apiBaseUrl.trim() ? apiBaseUrl : DEFAULT_IDENTITY_API_BASE).replace(/\/$/, '');

  return async (payload) => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${baseUrl}/api/bugs/intake`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Bug report intake failed (${response.status})`);
    }

    const data = await response.json() as {
      id?: string;
      status?: string;
      duplicate_of_ticket_id?: string | null;
      github?: { issue_url?: string | null };
    };

    return {
      ticketId: String(data.id ?? ''),
      status: data.status,
      duplicateOfTicketId: data.duplicate_of_ticket_id ?? null,
      githubIssueUrl: data.github?.issue_url ?? null,
    };
  };
}

export function useTrainingTrackerBugReport(user: User | null): BugReportConfig | null {
  return useMemo(() => {
    if (isDemoMode) {
      return null;
    }

    return {
      appSlug: 'training-tracker',
      modalTitle: 'Report a Training Tracker Bug',
      autoCapture: true,
      submitReport: createIdentitySubmitter({
        getAuthToken: async () => firebaseAuth?.currentUser?.getIdToken() ?? null,
        apiBaseUrl: import.meta.env.VITE_IDENTITY_API_URL as string | undefined,
      }),
      getReporter: () => (
        user
          ? {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              role: user.role,
            }
          : undefined
      ),
      getRelease: () => ({
        version: import.meta.env.VITE_APP_VERSION || 'dev',
        build: import.meta.env.VITE_APP_VERSION || 'dev',
        commit: import.meta.env.VITE_SENTRY_RELEASE || null,
        environment: import.meta.env.MODE,
      }),
      getStateSnapshot: () => ({
        auth: user
          ? {
              uid: user.uid,
              role: user.role,
              identityPersonId: user.identityPersonId ?? null,
            }
          : null,
        mode: {
          demo: isDemoMode,
          pathname: window.location.pathname,
        },
        monitoring: {
          eventCounts: monitoring.getEventCounts(),
          recentEvents: monitoring.getEvents({ limit: 12 }),
          recentMetrics: monitoring.getMetrics(5),
        },
      }),
      getDiagnostics: () => {
        const recentErrors: RecentBugError[] = monitoring
          .getEvents({ type: 'error', limit: 5 })
          .map((event) => ({
            message: event.message,
            stack: event.stack ?? null,
            timestamp: event.timestamp,
          }));

        const consoleLogs = (sharedBugReporting as { getRecentConsoleLogs?: (limit?: number) => unknown[] }).getRecentConsoleLogs?.(50) ?? [];
        return {
          recentErrors,
          consoleLogs,
          breadcrumbs: monitoring.getBreadcrumbs(20),
          sentry: {
            lastEventId: Sentry.lastEventId() || null,
            replayId: getReplayId(),
          },
          extra: {
            eventCounts: monitoring.getEventCounts(),
          },
        };
      },
    };
  }, [user]);
}
